import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsService } from '@ddt-web/analytics';
import { ERROR_SEVERITY, ErrorDCR } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { AbstractErrorHandlerService, ErrorHandlerUtil, ErrorHelperService, ErrorObject, ErrorPopupService } from '@ddt-web/error-handler';

import { FundTagData } from '../models';

/**
 * This error handler listens for and handles errors which occur during the fund tag selection action that the user takes.
 * These errors include failure to update tags due to missing required data points, failure to update tags due to unsuccessful API calls & tag limit exceeded.
 * All tag update errors are of warning severity - the user will just be notified of the issue and be allowed to continue analysis.
 * 
 * This is a commonly shared error handler.
 * Add it to the chain of handlers in app.module.ts like:
 * ErrorHandlerModule.forRoot({ useErrorHandlers: [IqErrorHandlerService, FundTagErrorHandlerService]})
 * If you want to customize, please add to application level handler
 * e.g. iq-error-handler.service
 */
@Injectable()
export class FundTagErrorHandlerService extends AbstractErrorHandlerService {

	private static readonly TRACK_ERROR_MESSAGES: { [configName: string]: string } = {
		[ErrorDCR.FUND_TAG_MISSING_ID_ERROR]: 'missingfundtagid',
		[ErrorDCR.FUND_TAG_API_FAILURE_ERROR]: 'fundtagapifailure',
		[ErrorDCR.FUND_TAG_LIMIT_EXCEEDED_ERROR]: 'fundtaglimitexceeded',
		[ErrorDCR.FUND_TAG_TAGGED_FUND_LIMIT_EXCEEDED_ERROR]: 'fundtagtaggedfundlimitexceeded',
		[ErrorDCR.FUND_TAG_MISSING_SECONDARY_IDENTIFIERS_ERROR]: 'missingsecondaryfundtagidentifiers'
	};

	@InjectConfig(SharedConfigKey.MAXIMUM_TAGS_PER_FUND) private readonly maxTagsPerFund: number = 10;
	@InjectConfig(SharedConfigKey.MAXIMUM_TAGGED_FUNDS) private readonly maxTaggedFunds: number = 250;

	constructor(
		protected contentService: ContentService,
		protected errorPopupService: ErrorPopupService,
		protected tealiumAnalyticsService: TealiumAnalyticsService,
		protected errorHelperService: ErrorHelperService
	) {
		super(contentService, errorPopupService, tealiumAnalyticsService, errorHelperService);
	}

	public handleError(error: ErrorObject): void {
		const isFundTagError: boolean = Object.keys(FundTagErrorHandlerService.TRACK_ERROR_MESSAGES).includes(error.configName);

		if (isFundTagError) {
			this.errorSeverity = ERROR_SEVERITY.WARNING;
			this.trackErrorMessage = FundTagErrorHandlerService.TRACK_ERROR_MESSAGES[error.configName];
			this.tealiumAnalyticsService.trackError(this.trackErrorMessage, this.errorSeverity);

			if (error.configName === ErrorDCR.FUND_TAG_MISSING_SECONDARY_IDENTIFIERS_ERROR) {
				console.warn(
					'Fund tag data is missing some of the secondary fund identifiers. Will proceed storing tags but the fund identifier data in the tag records will be incomplete.',
					error
				);
			} else {
				const tickers: string[] = (error.responseJson.fundPreferences as FundTagData[] ?? []).map(tagData => tagData.ticker).filter(ticker => !!ticker);

				let message;
				if (error.configName === ErrorDCR.FUND_TAG_TAGGED_FUND_LIMIT_EXCEEDED_ERROR) {
					message = this.contentService.getSubstitutedValuesString(
						'FUND_TAG_TAGGED_FUND_LIMIT_EXCEEDED',
						new Map([['$maxLimit$', this.maxTaggedFunds.toString()]]),
						'You may only have $maxLimit$ tagged funds at a time. Please remove some of your tagged funds to proceed.'
					);
				} else if (error.configName === ErrorDCR.FUND_TAG_LIMIT_EXCEEDED_ERROR) {
					message = ErrorHandlerUtil.getUserErrorMessage(
						{
							dcrKey: 'FUND_TAG_LIMIT_EXCEEDED',
							dcrValue: 'You have selected more than the maximum limit of tags for the following funds: $tickers$. Please select a maximum of $maxLimit$ tags.'
						},
						{
							dcrKey: 'FUND_TAG_LIMIT_EXCEEDED_GENERIC',
							dcrValue: 'You have selected more than the maximum limit of tags for your funds. Please select a maximum of $maxLimit$ tags.'
						},
						new Map([['$tickers$', tickers.join(', ')], ['$maxLimit$', this.maxTagsPerFund.toString()]]),
						!tickers.length,
						this.contentService
					);
				} else {
					message = ErrorHandlerUtil.getUserErrorMessage(
						{ dcrKey: 'FUND_TAG_STORAGE_FAILURE', dcrValue: 'Failed to update tags for the following funds: $tickers$' },
						{ dcrKey: 'FUND_TAG_STORAGE_GENERIC_FAILURE', dcrValue: 'Something went wrong while storing tags. Please retry.' },
						new Map([['$tickers$', tickers.join(', ')]]),
						!tickers.length,
						this.contentService
					);
				}
				const actions = [ErrorHandlerUtil.createErrorContinueButton(this.contentService)];

				console.error(`${message} - ${error.configName}`, error);
				this.errorPopupService.openResponseMessageDialog(
					this.buildErrorMessage(false, 'error-fund-tags-dialog', this.errorMessage, this.trackErrorMessage, message, true, tickers, actions)
				);
			}
		} else {
			this.nextHandler.handleError(error);
		}
	}
}
